import { Settings } from 'luxon'
import * as types from '../mutation-types'
import i18n from '../../i18n'
import uuid4 from 'uuid/v4'

// initial state
export const initialState = () => {
  return {
    frontendSessionId: uuid4(),
    activeStep: 1,
    campground: null,
    currency: {},
    langSlug: null,
    language: {},
    loader: false,
    showBookingDetails: false,
    testmode: null,
    steps: {
      1: {
        displaySelection: 'dates',
        fulfilled: false,
        id: 1,
      },
      2: {
        id: 2,
        selectedCampsiteType: null,
        selectedCampsiteIsAvailable: true,
        fulfilled: false,
      },
      3: {
        id: 3,
        fulfilled: true,
      },
      4: {
        id: 4,
        formPart1Passed: false,
        fulfilled: false,
      },
      5: {
        id: 5,
        fulfilled: false,
      },
    },
  }
}
let state = initialState()

// getters
const getters = {
  testmode: state => state.testmode,
  activeStep: state => state.activeStep,
  campground: state => state.campground,
  currency: state => state.currency,
  langSlug: state => state.langSlug,
  language: state => state.language,
  loader: state => state.loader,
  showBookingDetails: state => state.showBookingDetails,
  steps: state => state.steps,
  frontendSessionId: state => state.frontendSessionId,
}

// actions
const actions = {
  setTestmode({ commit }, value) {
    commit(types.APP_SET_TESTMODE, value)
  },
  setLanguageSlug({ commit }, value) {
    commit(types.APP_SET_LANGUAGE_SLUG, value)
  },
  setLanguage({ commit, dispatch }, value) {
    commit(types.APP_SET_LANGUAGE, value)
    dispatch('campground/checkPrice', false, { root: true })
  },
  setCurrency({ commit, dispatch, rootGetters }, currency) {
    commit(types.APP_SET_LOADER, true)
    const dates = rootGetters['booking/dates']
    const persons = rootGetters['booking/selectedPersons']
    const isCompleted = rootGetters['booking/isCompleted']

    /* no need to do any price or availability checks is booking is completed */
    if (isCompleted) {
      commit(types.APP_SET_CURRENCY, currency)
      commit(types.APP_SET_LOADER, false)
      return
    }

    if (dates && dates.length === 2 && persons.length > 0) {
      Promise.all([
        dispatch('campground/getExtraPrices', currency, { root: true }),
        dispatch('campground/getAvailabilities', currency, { root: true }),
        dispatch('campground/checkPrice', currency, { root: true }),
      ]).then(() => {
        commit(types.APP_SET_CURRENCY, currency)
        commit(types.APP_SET_LOADER, false)
      })
    } else {
      dispatch('campground/getExtraPrices', currency, { root: true })
      commit(types.APP_SET_CURRENCY, currency)
      commit(types.APP_SET_LOADER, false)
    }
  },
  setCampground({ commit }, value) {
    commit(types.APP_SET_CAMPGROUND, value)
  },
  setStep({ commit, state, dispatch }, newStep) {
    /* if you have passed step 4 and return to a lower state, also rest fromPart1Passed */
    /* istanbul ignore else */
    if (state.activeStep >= 4 && newStep <= 4) {
      commit(types.APP_SET_STEP_STATUS, { number: 4, key: 'formPart1Passed', value: false })
    }

    /* if newStep is payment - clear adyen & status - check stored checkout */
    /* istanbul ignore else */
    if (newStep === 5) {
      dispatch('booking/resetAdyen', null, { root: true })
      dispatch('booking/resetStatus', null, { root: true })
      dispatch('booking/handleStoredCheckout', null, { root: true })
    }

    /* in any case you where the new step isn't step 6 (confirmation), set completed to false */
    /* istanbul ignore else */
    if (newStep !== 6) {
      dispatch('booking/setCompleted', false, { root: true })
    }

    commit(types.APP_SET_STEP, newStep)
  },
  setStepStatus({ commit }, { number, key, value }) {
    commit(types.APP_SET_STEP_STATUS, { number, key, value })
  },
  setLoader({ commit }, value) {
    commit(types.APP_SET_LOADER, value)
  },
  toggleBookingDetails({ commit }) {
    commit(types.APP_TOGGLE_BOOKING_DETAILS)
  },
  resetStepStatus({ commit }) {
    const initState = initialState()
    commit(types.APP_RESET_STEP_STATUS, initState.steps)
  },
  resetState({ commit }) {
    const initState = initialState()
    commit(types.APP_RESET_STATE, initState)
  },
  setTitle({ commit, rootGetters }) {
    setTimeout(() => {
      if (rootGetters['campground/campground'] && rootGetters['campground/campground'].name) {
        commit(types.APP_SET_TITLE, rootGetters['campground/campground'].name)
      }
    }, 0)
  },
}

// mutations
const mutations = {
  [types.APP_RESET_STATE](state, initState) {
    Object.keys(initState).forEach(key => {
      state[key] = initState[key]
    })
  },
  [types.APP_SET_CURRENCY](state, value) {
    state.currency = value
  },
  [types.APP_SET_TESTMODE](state, value) {
    state.testmode = value
  },
  [types.APP_SET_LANGUAGE_SLUG](state, value) {
    state.langSlug = value
    i18n.locale = value
    Settings.defaultLocale = value
  },
  [types.APP_SET_LANGUAGE](state, value) {
    state.language = value
  },
  [types.APP_SET_CAMPGROUND](state, value) {
    state.campground = value
  },
  [types.APP_SET_STEP](state, payload) {
    state.activeStep = payload
  },
  [types.APP_SET_LOADER](state, payload) {
    state.loader = payload
  },
  [types.APP_TOGGLE_BOOKING_DETAILS](state) {
    state.showBookingDetails = !state.showBookingDetails
  },
  [types.APP_SET_STEP_STATUS](state, { number, key, value }) {
    const step = { ...state.steps[number], [key]: value }
    state.steps = { ...state.steps, [number]: step }
  },
  [types.APP_RESET_STEP_STATUS](state, steps) {
    state.steps = steps
  },
  [types.APP_SET_TITLE](state, campgroundName) {
    if (campgroundName) {
      document.title = `${i18n.t('meta.title', { campground: campgroundName })}`
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
